import * as Tabs from '@radix-ui/react-tabs';
import { FiX,  FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import BorderGradient from 'components/shared/BorderGradient/BorderGradient';
import { useState, useEffect } from 'react';
import Alert from 'components/shared/Alert';
import SubscriberModal from './SubscriberModal';
import DatePicker from 'react-datepicker';

interface SubscriptionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriber: any;
}

export default function SubscriptionDetailsModal({ isOpen, onClose, subscriber }: SubscriptionDetailsModalProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAlert, setShowAlert] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ show: false, message: '', type: 'success' });
  const [showDetailsModal, setShowDetailsModal] = useState(isOpen);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setShowDetailsModal(isOpen);
  }, [isOpen]);

  const handleRenew = () => {
    setShowAlert({
      show: true,
      message: 'Congratulations, your Subscription is renewed!',
      type: 'success'
    });
    setTimeout(() => {
      setShowAlert(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleCancel = () => {
    setShowAlert({
      show: true,
      message: 'Subscription got cancelled',
      type: 'error'
    });
    setTimeout(() => {
      setShowAlert(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
    setSearchTerm(date.toLocaleDateString());
  };

  const handleEdit = () => {
    setShowDetailsModal(false);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setShowDetailsModal(true);
  };

  if (!subscriber) return null;

  return (
    <>
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <BorderGradient className="rounded-lg flex flex-col">
            <div className="w-[1000px] bg-[#1A1A1A] rounded-lg overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-white/[0.08]">
                <h2 className="text-xl font-semibold text-white">Subscription Details</h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={handleEdit}
                    className="px-4 py-2 text-sm text-white bg-[#2A2A2A] rounded-md hover:bg-[#3a3a3a33]"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={handleRenew}
                    className="px-4 py-2 text-sm text-white bg-[#2A2A2A] rounded-md hover:bg-[#3a3a3a33]"
                  >
                    Renew
                  </button>
                  <button 
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm text-white bg-[#2A2A2A] rounded-md hover:bg-[#3a3a3a33]"
                  >
                    Cancel
                  </button>
                  <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <FiX size={24} />
                  </button>
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-6 border-b border-white/[0.08]">
                <div className="flex items-start gap-6">
                  <img src="/images/pS.png" alt="Profile" className="w-20 h-20 rounded-full" />
                  <div className="flex-1">
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Customer ID</p>
                        <p className="text-white">ID-011221</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Customer Email</p>
                        <p className="text-white">Johndoe@gmail.com</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Phone Number</p>
                        <p className="text-white">050 414 8778</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Company</p>
                        <p className="text-white">Matrices ltd.</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-gray-400 mb-1">Address</p>
                        <p className="text-white">1833 Bel Meadow Drive, Fontana, California 92335, USA</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-6 mt-6">
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Subscribed to</p>
                        <p className="text-white">Plan 1</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Amount Charged</p>
                        <p className="text-white">$ 140/month</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">Subscription Status</p>
                        <p className="text-white">Active</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400 mb-1">VAT number</p>
                        <p className="text-white">0123123123</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <Tabs.Root defaultValue="subscription-details" className="p-6">
                <Tabs.List className="flex gap-6 border-b border-white/[0.08]">
                  <Tabs.Trigger 
                    value="subscription-details"
                    className="pb-4 text-gray-400 hover:text-white data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-white"
                  >
                    Subscription details
                  </Tabs.Trigger>
                  <Tabs.Trigger 
                    value="billing-details"
                    className="pb-4 text-gray-400 hover:text-white data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-white"
                  >
                    Billing details
                  </Tabs.Trigger>
                  <Tabs.Trigger 
                    value="customer-history"
                    className="pb-4 text-gray-400 hover:text-white data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-white"
                  >
                    Customer history
                  </Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content value="subscription-details" className="pt-6">
                  <div className="grid grid-cols-3 gap-6">
                    {/* Basic details */}
                    <div className="border border-white/[0.08] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-4">Basic details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Billing cycle</span>
                          <span className="text-white">Monthly</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Autorenewal</span>
                          <span className="text-white">Yes</span>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="border border-white/[0.08] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-4">Features</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Create Tags</span>
                          <span className="text-white">Up to 20</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Add Users</span>
                          <span className="text-white">Up to 5 users</span>
                        </div>
                      </div>
                    </div>

                    {/* Usage limits */}
                    <div className="border border-white/[0.08] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-4">Usage limits</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Users</span>
                          <span className="text-white">Up to 20</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Storage</span>
                          <span className="text-white">10GB</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Other sections */}
                  <div className="grid grid-cols-2 gap-6 mt-6">
                    <div className="border border-white/[0.08] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-4">Upgrade/Downgrade</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Downgrade</span>
                          <span className="text-white">Applicable</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Upgrade</span>
                          <span className="text-white">Applicable</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-white/[0.08] rounded-lg p-4">
                      <h3 className="text-white font-medium mb-4">Support</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Support level</span>
                          <span className="text-white">Email</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms and conditions */}
                  <div className="border border-white/[0.08] rounded-lg p-4 mt-6">
                    <h3 className="text-white font-medium mb-4">Terms and conditions</h3>
                    <p className="text-gray-400">
                      &quot;Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
                      dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
                      commodo consequat.&quot;
                    </p>
                  </div>
                </Tabs.Content>

                <Tabs.Content value="billing-details" className="pt-6">
                  <div className="border border-white/[0.08] rounded-lg">
                    <div className="flex justify-between items-center p-4 border-b border-white/[0.08]">
                      <h3 className="text-white font-medium">Billing History</h3>
                      <button className="text-sm text-gray-400 hover:text-white">View all</button>
                    </div>
                    <table className="w-full">
                      <thead>
                        <tr className="text-sm text-gray-400 border-b border-white/[0.08]">
                          <th className="text-left p-4">Invoice no.</th>
                          <th className="text-left p-4">Status</th>
                          <th className="text-left p-4">Start Date</th>
                          <th className="text-left p-4">End Date</th>
                          <th className="text-left p-4">Invoice Date</th>
                          {/* <th className="text-left p-4">Due Date</th> */}
                          <th className="text-left p-4">Payment Status</th>
                          <th className="text-left p-4">Payment method</th>
                          {/* <th className="text-right p-4">Actions</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          {
                            id: '0007',
                            status: 'Pending',
                            startDate: '12/03/2025',
                            endDate: '12/03/2025',
                            invoiceDate: '12/03/2025',
                            dueDate: '12/06/2024',
                            paymentStatus: 'Failed',
                            paymentMethod: 'Credit card'
                          },
                          {
                            id: '0006',
                            status: 'Active',
                            startDate: '12/03/2025',
                            endDate: '12/03/2025',
                            invoiceDate: '12/03/2025',
                            dueDate: '12/06/2024',
                            paymentStatus: 'Successful',
                            paymentMethod: 'Credit card'
                          },
                          // Add more invoice data...
                        ].map((invoice, index) => (
                          <tr key={invoice.id} className="border-b border-white/[0.08] hover:bg-[#2A2A2A]">
                            <td className="p-4 text-white">{invoice.id}</td>
                            <td className="p-4 text-gray-400">{invoice.status}</td>
                            <td className="p-4 text-gray-400">{invoice.startDate}</td>
                            <td className="p-4 text-gray-400">{invoice.endDate}</td>
                            <td className="p-4 text-gray-400">{invoice.invoiceDate}</td>
                            {/* <td className="p-4 text-red-400">{invoice.dueDate}</td> */}
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                invoice.paymentStatus === 'Failed' 
                                  ? 'bg-[#FF0000]/40 text-white'
                                  : 'bg-[#008000]/40 text-white'
                              }`}>
                                {invoice.paymentStatus}
                              </span>
                            </td>
                            <td className="p-4 text-gray-400">{invoice.paymentMethod}</td>
                            {/* <td className="p-4 text-right">
                              <button className="text-gray-400 hover:text-white">
                                <FiMail size={16} />
                              </button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="flex items-center justify-between p-4 border-t border-white/[0.08]">
                      <div className="text-sm text-gray-400">
                        Showing 1 to 7 of 7 results
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          disabled={true}
                          className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiChevronLeft size={20} />
                        </button>
                        
                        <div className="flex gap-1">
                          <button className="w-8 h-8 rounded-lg text-sm font-medium bg-[#2A2A2A] text-white">
                            1
                          </button>
                        </div>

                        <button
                          disabled={true}
                          className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiChevronRight size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                </Tabs.Content>

                <Tabs.Content value="customer-history" className="pt-6">
                  <div className="border border-white/[0.08] rounded-lg p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-white font-medium">Activity Timeline</h3>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="bg-[#2A2A2A] rounded-lg px-4 py-2 text-white border border-white/[0.08] w-64"
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <button 
                            className="text-gray-400 hover:text-white"
                            onClick={() => setShowDatePicker(!showDatePicker)}
                          >
                            <FiCalendar size={16} />
                          </button>
                        </div>
                        {showDatePicker && (
                          <div className="absolute right-0 mt-2 z-50">
                            <DatePicker
                              selected={selectedDate}
                              onChange={handleDateSelect}
                              inline
                              dateFormat="MM/dd/yyyy"
                              className="bg-[#2A2A2A] border border-white/[0.08] rounded-lg"
                              calendarClassName="bg-[#2A2A2A] border border-white/[0.08] rounded-lg text-white"
                              dayClassName={date => 
                                "text-white hover:bg-[#3a3a3a33] rounded-lg"
                              }
                              monthClassName={() => "text-white"}
                              weekDayClassName={() => "text-gray-400"}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* March Section */}
                      <div>
                        <h4 className="text-white mb-4">March</h4>
                        <div className="relative pl-6">
                          {/* Timeline line */}
                          <div className="absolute left-[9px] top-3 bottom-0 w-[2px] bg-white/[0.08]" />
                          
                          {/* Timeline items */}
                          <div className="space-y-8">
                            <div className="relative">
                              {/* Timeline dot */}
                              <div className="absolute left-[-23px] top-8 w-5 h-5 rounded-full bg-[#2A2A2A] border-2 border-white/[0.08]" />
                              <div className="bg-[#2A2A2A] rounded-lg p-4">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h5 className="text-white">Emailed - Regarding Moving To Plan B</h5>
                                    <p className="text-gray-400 text-sm">plan 1</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white">01:30 PM</p>
                                    <p className="text-gray-400 text-sm">10 March 2024</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="relative">
                              <div className="absolute left-[-23px] top-8 w-5 h-5 rounded-full bg-[#2A2A2A] border-2 border-white/[0.08]" />
                              <div className="bg-[#2A2A2A] rounded-lg p-4">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h5 className="text-white">Ticket Created</h5>
                                    <p className="text-gray-400 text-sm">#0123</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white">01:30 PM</p>
                                    <p className="text-gray-400 text-sm">10 March 2024</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="relative">
                              <div className="absolute left-[-23px] top-8 w-5 h-5 rounded-full bg-[#2A2A2A] border-2 border-white/[0.08]" />
                              <div className="bg-[#2A2A2A] rounded-lg p-4">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h5 className="text-white">First Payment Done</h5>
                                    <p className="text-gray-400 text-sm">plan 1</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white">01:30 PM</p>
                                    <p className="text-gray-400 text-sm">10 March 2024</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* April Section */}
                      <div>
                        <h4 className="text-white mb-4">April</h4>
                        <div className="relative pl-6">
                          <div className="absolute left-[9px] top-3 bottom-0 w-[2px] bg-white/[0.08]" />
                          
                          <div className="space-y-8">
                            <div className="relative">
                              <div className="absolute left-[-23px] top-8 w-5 h-5 rounded-full bg-[#2A2A2A] border-2 border-white/[0.08]" />
                              <div className="bg-[#2A2A2A] rounded-lg p-4">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h5 className="text-white">Subscription Activated</h5>
                                    <p className="text-gray-400 text-sm">plan 1</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white">01:30 PM</p>
                                    <p className="text-gray-400 text-sm">10 April 2024</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tabs.Content>
              </Tabs.Root>
            </div>
          </BorderGradient>
        </div>
      )}

      {/* Edit Modal */}
      <SubscriberModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        mode="edit"
        initialData={subscriber}
      />

      {/* Alert */}
      <Alert
        isOpen={showAlert.show}
        onClose={() => setShowAlert(prev => ({ ...prev, show: false }))}
        message={showAlert.message}
        type={showAlert.type}
      />
    </>
  );
} 